<template>
  <div class="sidebar-resume">
    <!--<h3 class="h3">Riepilogo tariffa</h3>-->
    <div v-for="(flight_offer, index) in flight_offers" :key="'price_' + index">
      <h3 class="h6 bold mb-3 pt-2">La tua scelta:</h3>
      <div class="d-flex flex-column gap-30px">
        <!-- base -->
        <sidebar-resume-block
          title="Tariffa:"
          :title_content="{
            icon: svg_flight_ticket('#35A8E0'),
            text: branded_fare(flight_offer),
          }"
        >
          <template #default>
            <sidebar-row
              v-for="(group, age) in travelers_by_age(flight_offer)"
              :key="`group:${age}`"
              :label="`${group.list.length} ${transcode_traveler_type(age, group.list.length)}`"
              :content="group.total"
              :currency_verbose="flight_offer.price.currency"
            />
            <sidebar-row
              label="Tasse e spese"
              :content="get_total_tax(flight_offer)"
              :currency_verbose="flight_offer.price.currency"
            />
          </template>
          <template #footer>
            <sidebar-row
              label="Totale"
              classes="semibold"
              :content="get_base_total(flight_offer)"
              :currency_verbose="flight_offer.price.currency"
            />
          </template>
        </sidebar-resume-block>

        <!-- included bags -->
        <sidebar-resume-block
          title="Bagagli inclusi nella tariffa"
          info="I bagagli indicati si riferiscono al totale per tutti i passeggeri e tratte. Verifica sul sito della compagnia peso, misure e condizioni esatte."
        >
          <template #default>
            <sidebar-row
              label="Bagaglio personale"
              :content="flight_offer.travelerPricings.length"
              :icon="svg_backpack"
              info="È un piccolo bagaglio personale, come uno zaino o una borsa, da tenere sotto il sedile."
            />
            <sidebar-row
              v-if="flight_offer_has_upsell && branded_fare(flight_offer).length"
              label="Bagaglio a mano"
              :content="get_all_included_cabin_bags(flight_offer)"
              :icon="svg_trolley"
            >
              <template #content>
                <span
                  v-b-popover.hover.top="
                    `Controlla nella descrizione della tariffa ${
                      branded_fare(flight_offer) || 'che hai selezionato'
                    } quanti bagagli a mano sono inclusi.`
                  "
                  v-html="info"
                ></span>
              </template>
            </sidebar-row>
            <sidebar-row
              label="Bagaglio da stiva"
              :content="get_all_included_bags(flight_offer)"
              :icon="svg_luggage"
            />
          </template>
        </sidebar-resume-block>
      </div>
      <div
        v-if="get_paid_additional_services(flight_offer).length > 0"
        class="d-flex flex-column gap-30px"
        style="padding-top: 30px"
      >
        <div
          v-for="additional_service in get_paid_additional_services(flight_offer)"
          :key="`additional_service:${additional_service.type}`"
        >
          <sidebar-resume-block
            :title="capitalize_string(transcode_additional_services(additional_service.type, 99))"
            :info="capitalize_string(get_additional_service_tooltip(additional_service.type))"
          >
            <template v-if="additional_service.type === 'CHECKED_BAGS'" #default>
              <sidebar-row
                v-for="[key, extra_bag] in Object.entries(flexible_bags)"
                :key="key"
                :label="`Bagagli${
                  extra_bag.quantity * extra_bag.travelers.length === 1 ? 'o' : ''
                } in stiva`"
                :content="extra_bag.quantity * extra_bag.travelers.length"
                :icon="svg_luggage"
              />
            </template>
            <template v-else-if="additional_service.type === 'SEATS'" #default>
              <sidebar-row
                v-for="(seats, key) in seats_per_segment(flight_offer, traveler_details)"
                :key="`seats:${key}`"
                :label="formatSegmentLabel(segmentsMap[key])"
                :icon="svg_seats"
              >
                <template #content>
                  <span
                    v-for="seat in seats"
                    :key="`seat:${key}:${seat.chargeableSeatNumber}`"
                    v-b-popover.hover.top="seat.traveler_name || `P${seat.travelerId}`"
                    class="text-primary ml-1"
                  >
                    {{ seat.chargeableSeatNumber }}
                  </span>
                </template>
              </sidebar-row>
            </template>
            <template #footer>
              <sidebar-row
                label="Totale"
                classes="semibold"
                :content="Number(apply_markup_to_number(flight_offer, additional_service.amount))"
                :currency_verbose="flight_offer.price.currency"
              />
            </template>
          </sidebar-resume-block>
        </div>
      </div>

      <div id="resume-total" class="mt-5 d-flex justify-content-between">
        <h3 class="h3 mb-0">Totale</h3>
        <h3 class="h3 mb-0">
          {{ currency(flight_offer.price.currency) }}
          {{ formatPrice(get_markup(flight_offer).mu_price) }}
        </h3>
      </div>

      <div>
        <p class="mb-2">Tariffa, tasse e spese amministrative per tutti i partecipanti</p>
      </div>

      <div v-if="post_sell_notice.text">
        <p>
          {{ post_sell_notice.text }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  get_markup,
  apply_markup_to_number,
  transcode_traveler_type,
  parse_fligth_offer_bags,
  transcode_additional_services,
  get_additional_service_tooltip,
  get_paid_additional_services,
  seats_per_segment,
} from '@/flight-offer';
import { info } from '@/components/svg/common';
import { flight_ticket } from '@/components/svg/flight';
import { taxes } from '@/resources/taxes';
import { getSegmentsFromFlightOffers } from '../../../utilities/seat-map';
import { formatPrice, is_infant, capitalize, currency, formatSegmentLabel } from '@/utilities';
import { number, subtract } from 'mathjs';
import { backpack, luggage, trolley } from '@/components/svg/bags';
import { svgSeatChoice } from '@/components/svg/seat-map';

import SidebarResumeBlock from '@/components/blocks/sidebar/sidebar-resume-block.vue';
import SidebarRow from '@/components/blocks/sidebar/sidebar-row.vue';

export default {
  name: 'sidebar-resume',
  components: {
    'sidebar-resume-block': SidebarResumeBlock,
    'sidebar-row': SidebarRow,
  },
  methods: {
    capitalize_string: capitalize,
    svg_flight_ticket: flight_ticket,
    formatPrice,
    apply_markup_to_number,
    is_infant,
    currency,
    formatSegmentLabel,
    get_additional_service_tooltip,
    get_paid_additional_services,
    seats_per_segment,
    parse_fligth_offer_bags,
    get_all_included_bags(flight_offer) {
      return this.parse_fligth_offer_bags(flight_offer, 0).included;
    },
    get_all_included_cabin_bags(flight_offer) {
      return this.parse_fligth_offer_bags(flight_offer, 0).cabin;
    },
    travelers_by_age(flight_offer) {
      return flight_offer.travelerPricings.reduce((groups, pricing) => {
        const previous = groups[pricing.travelerType] || { list: [], total: 0 };
        return {
          ...groups,
          [pricing.travelerType]: {
            list: [...previous.list, pricing],
            total: previous.total + number((pricing.price || {}).total || 0),
          },
        };
      }, {});
    },
    get_total_tax(flight_offer) {
      const tax = this.get_markup(flight_offer);
      return subtract(tax.mu_amount, tax.additional_services_mu_amount);
    },
    get_travelers_total(flight_offer) {
      return Object.values(this.travelers_by_age(flight_offer)).reduce(
        (total, elem) => total + elem.total,
        0
      );
    },
    get_base_total(flight_offer) {
      return this.get_travelers_total(flight_offer) + this.get_total_tax(flight_offer);
    },
    branded_fare(flight_offer) {
      try {
        return this.capitalize_string(
          flight_offer.travelerPricings[0].fareDetailsBySegment[0].brandedFare
        );
      } catch {
        return '';
      }
    },
  },
  data() {
    return {
      segmentsMap: {},
      svg_backpack: backpack('#000', 14, 14),
      svg_trolley: trolley('#000', 12, 20),
      svg_luggage: luggage('#000', 12, 17),
      svg_seats: svgSeatChoice(17),
    };
  },
  props: {
    flight_offers: {
      type: Array,
      default: () => {
        return [];
      },
    },
    post_sell_notice: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    flight_offer_has_upsell() {
      try {
        return this.$store.state.liveflight.flight_offer_has_upsell;
      } catch {
        return false;
      }
    },
    flexible_bags() {
      const flexible_bags = this.$store.state.liveflight.flexible_bags;

      const mapped = {};
      Object.entries(flexible_bags).forEach((entry) => {
        const traveler_index = entry[0];
        const itineraries = Object.entries(entry[1]);
        itineraries.forEach((itinerary) => {
          const itinerary_index = itinerary[0];
          const bags = Object.entries(itinerary[1]);
          bags.forEach((elem) => {
            const bag_info = elem[1];

            if (bag_info.added) {
              const bag = bag_info.bag;
              const prev = mapped[`${bag.name}:${bag.quantity}`] || {
                itineraries: [],
                travelers: [],
              };
              mapped[`${bag.name}:${bag.quantity}`] = {
                ...bag,
                itineraries: !prev.itineraries.includes(itinerary_index)
                  ? [...prev.itineraries, itinerary_index]
                  : prev.itineraries,
                travelers: !prev.travelers.includes(traveler_index)
                  ? [...prev.travelers, traveler_index]
                  : prev.travelers,
              };
            }
          });
        });
      });

      return mapped;
    },
    traveler_details() {
      return this.$store.state.liveflight.traveler_details;
    },
  },
  created() {
    this.info = info;
    this.get_markup = get_markup;
    this.transcode_traveler_type = transcode_traveler_type;
    this.transcode_additional_services = transcode_additional_services;
    this.taxes = taxes;
    this.segmentsMap = getSegmentsFromFlightOffers(this.flight_offers);
  },
};
</script>

<style lang="scss">
.sidebar-resume {
  // reso coerente a padding di flight-pad
  // $cell-padding: $grid-gutter-width/4 $grid-gutter-width/2;
  //padding-top:5.4px;
  padding-bottom: 5.4px;

  p,
  .paragraph {
    font-size: 12px;
    margin-top: 10px;
    @include font-primary-regular;
  }

  &--fare {
    padding: 20px;
    padding-bottom: 0;
    width: calc(100% + 40px);
    margin-left: -20px;
    margin-right: -20px;
    margin-top: -1px;

    svg {
      path {
        fill: $primary;
      }
    }
  }

  &--info {
    display: inline-block;
    width: 15px;
    height: 15px;

    @include media-breakpoint-up(sm) {
      width: 13px;
      height: 13px;
    }
  }
}
.sidebar-resume--collapse--title {
  display: flex;
  justify-content: space-between;
  div.icon {
    transition: $transition-base;
    svg {
      width: 10px;
      height: auto;
    }
  }
  &.not-collapsed {
    margin-bottom: 1rem;
    div.icon {
      transform: rotate(180deg);
    }
  }
}
</style>
